import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { CheckCircle, XCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Unified handler for ALL Supabase auth callbacks:
 * - Email verification (signup confirmation)
 * - Password reset (recovery)
 * - Email change confirmation
 * - Magic link authentication
 */
const AuthCallbackHandler = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'reset-password'
  const [callbackType, setCallbackType] = useState(null); // 'recovery', 'signup', 'email_change', 'magiclink'
  const [error, setError] = useState('');
  const [isValidSession, setIsValidSession] = useState(false);

  // Password reset form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      console.log('🔐 Auth callback triggered');
      console.log('URL:', window.location.href);
      console.log('Hash:', window.location.hash);

      // Get session from Supabase (extracts token from URL hash automatically)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      console.log('Session:', session);
      console.log('Session error:', sessionError);

      if (sessionError) {
        console.error('Session error:', sessionError);
        setStatus('error');
        setError(sessionError.message || 'Invalid or expired link. Please try again.');
        return;
      }

      if (!session) {
        console.error('No session found');
        setStatus('error');
        setError('Invalid or expired link. Please request a new one.');
        return;
      }

      // Extract callback type from URL hash parameters
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');

      console.log('Callback type:', type);
      setCallbackType(type);

      if (type === 'recovery') {
        // Password reset flow - show inline form
        console.log('✅ Password reset session valid - showing reset form');
        setIsValidSession(true);
        setStatus('reset-password');
      } else if (type === 'signup' || type === 'email_change') {
        // Email verification or email change - redirect to login
        console.log('✅ Email verified - redirecting to login');

        // Set flag for onboarding (will be shown after login)
        try {
          localStorage.setItem('show_onboarding_after_verification', 'true');
          console.log('✅ Onboarding flag set:', localStorage.getItem('show_onboarding_after_verification'));
        } catch (e) {
          console.error('Failed to set onboarding flag:', e);
        }

        setStatus('success');

        // Show success for 2 seconds then redirect to login
        setTimeout(() => {
          console.log('🔄 Redirecting to login. Flag status:', localStorage.getItem('show_onboarding_after_verification'));
          navigate('/', { replace: true });
        }, 2000);
      } else {
        // Unknown type or magic link - just show success and redirect
        console.log('✅ Auth callback successful - redirecting');
        setStatus('success');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      }

    } catch (err) {
      console.error('Auth callback error:', err);
      setStatus('error');
      setError(err.message || 'An unexpected error occurred');
    }
  };

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score <= 1) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { score, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 4) return { score, label: 'Good', color: 'bg-blue-500' };
    return { score, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');

    // Validation
    if (!formData.password) {
      setResetError('Please enter a new password');
      return;
    }
    if (formData.password.length < 6) {
      setResetError('Password must be at least 6 characters long');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    if (!isValidSession) {
      setResetError('Invalid or expired reset link. Please request a new password reset.');
      return;
    }

    setResetLoading(true);

    try {
      // Update password using Supabase client
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (updateError) {
        throw updateError;
      }

      // Show success toast
      toast.success('Password successfully reset!', {
        description: 'You can now sign in with your new password',
        duration: 3000
      });

      // Sign out the user (they need to sign in with new password)
      await supabase.auth.signOut();

      // Redirect to login after 1.5 seconds
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);

    } catch (err) {
      const msg = err.message || 'Failed to reset password. Please try again.';
      setResetError(msg);
    } finally {
      setResetLoading(false);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email...</h2>
          <p className="text-gray-600">This will only take a moment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-50 transition-all"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state (email verified)
  if (status === 'success') {
    const isSignup = callbackType === 'signup';

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Email Verified!</h2>

          {isSignup ? (
            <>
              <p className="text-gray-700 mb-2">
                Your email has been successfully confirmed.
              </p>
              <p className="text-gray-600 mb-6">
                Redirecting you to sign in and start your learning journey...
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-6">
                Redirecting you back to the app...
              </p>
            </>
          )}

          <div className="space-y-3">
            <div className="animate-pulse">
              <div className="h-2 bg-green-200 rounded-full w-full"></div>
            </div>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-green-700 hover:to-blue-700 transition-all"
            >
              Continue to ScrollEd
            </button>
          </div>
        </div>
      </div>
    );
  }

  // // Password reset form state
  // if (status === 'reset-password') {
  //   return (
  //     <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
  //       <div className="w-full max-w-md">
  //         {/* Header */}
  //         <div className="text-center mb-8">
  //           <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
  //             <Lock className="w-8 h-8 text-white" />
  //           </div>
  //           <h1 className="text-2xl font-bold text-gray-900 mb-2">Create New Password</h1>
  //           <p className="text-gray-600">
  //             Enter your new password below
  //           </p>
  //         </div>

  //         {/* Form */}
  //         <div className="bg-white rounded-2xl shadow-xl p-6 space-y-6">
  //           <form onSubmit={handlePasswordReset} className="space-y-4">
  //             {/* Error Display */}
  //             {resetError && (
  //               <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
  //                 <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
  //                 <p className="text-red-800 text-sm">{resetError}</p>
  //               </div>
  //             )}

  //             {/* New Password Field */}
  //             <div>
  //               <label className="block text-sm font-medium text-gray-700 mb-2">
  //                 New Password
  //               </label>
  //               <div className="relative">
  //                 <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
  //                   <Lock className="w-5 h-5 text-gray-400" />
  //                 </div>
  //                 <input
  //                   type={showPassword ? 'text' : 'password'}
  //                   value={formData.password}
  //                   onChange={(e) => {
  //                     setFormData({ ...formData, password: e.target.value });
  //                     setResetError('');
  //                   }}
  //                   className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
  //                   placeholder="Enter new password"
  //                   disabled={resetLoading}
  //                 />
  //                 <button
  //                   type="button"
  //                   onClick={() => setShowPassword(!showPassword)}
  //                   className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
  //                   disabled={resetLoading}
  //                 >
  //                   {showPassword ? (
  //                     <EyeOff className="w-5 h-5" />
  //                   ) : (
  //                     <Eye className="w-5 h-5" />
  //                   )}
  //                 </button>
  //               </div>

  //               {/* Password Strength Indicator */}
  //               {formData.password && (
  //                 <div className="mt-2">
  //                   <div className="flex items-center justify-between mb-1">
  //                     <span className="text-xs text-gray-600">Password strength:</span>
  //                     <span className={`text-xs font-medium ${
  //                       passwordStrength.score <= 1 ? 'text-red-600' :
  //                       passwordStrength.score <= 3 ? 'text-yellow-600' :
  //                       passwordStrength.score <= 4 ? 'text-blue-600' :
  //                       'text-green-600'
  //                     }`}>
  //                       {passwordStrength.label}
  //                     </span>
  //                   </div>
  //                   <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
  //                     <div
  //                       className={`h-full transition-all duration-300 ${passwordStrength.color}`}
  //                       style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
  //                     />
  //                   </div>
  //                   <p className="text-xs text-gray-500 mt-1">
  //                     Use 10+ characters with mix of letters, numbers & symbols
  //                   </p>
  //                 </div>
  //               )}
  //             </div>

  //             {/* Confirm Password Field */}
  //             <div>
  //               <label className="block text-sm font-medium text-gray-700 mb-2">
  //                 Confirm New Password
  //               </label>
  //               <div className="relative">
  //                 <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
  //                   <Lock className="w-5 h-5 text-gray-400" />
  //                 </div>
  //                 <input
  //                   type={showConfirmPassword ? 'text' : 'password'}
  //                   value={formData.confirmPassword}
  //                   onChange={(e) => {
  //                     setFormData({ ...formData, confirmPassword: e.target.value });
  //                     setResetError('');
  //                   }}
  //                   className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
  //                   placeholder="Confirm new password"
  //                   disabled={resetLoading}
  //                 />
  //                 <button
  //                   type="button"
  //                   onClick={() => setShowConfirmPassword(!showConfirmPassword)}
  //                   className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
  //                   disabled={resetLoading}
  //                 >
  //                   {showConfirmPassword ? (
  //                     <EyeOff className="w-5 h-5" />
  //                   ) : (
  //                     <Eye className="w-5 h-5" />
  //                   )}
  //                 </button>
  //               </div>

  //               {/* Password Match Indicator */}
  //               {formData.confirmPassword && (
  //                 <div className="mt-2 flex items-center space-x-2">
  //                   {formData.password === formData.confirmPassword ? (
  //                     <>
  //                       <CheckCircle className="w-4 h-4 text-green-600" />
  //                       <span className="text-xs text-green-600">Passwords match</span>
  //                     </>
  //                   ) : (
  //                     <>
  //                       <XCircle className="w-4 h-4 text-red-600" />
  //                       <span className="text-xs text-red-600">Passwords don't match</span>
  //                     </>
  //                   )}
  //                 </div>
  //               )}
  //             </div>

  //             {/* Submit Button */}
  //             <button
  //               type="submit"
  //               disabled={resetLoading}
  //               className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 focus:ring-4 focus:ring-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
  //             >
  //               {resetLoading ? (
  //                 <>
  //                   <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
  //                   <span>Resetting Password...</span>
  //                 </>
  //               ) : (
  //                 <>
  //                   <CheckCircle className="w-5 h-5" />
  //                   <span>Reset Password</span>
  //                 </>
  //               )}
  //             </button>
  //           </form>
  //         </div>

  //         {/* Security Note */}
  //         <div className="mt-6 p-4 bg-gray-50 rounded-xl">
  //           <p className="text-gray-600 text-sm text-center">
  //             For your security, this link will expire in 1 hour
  //           </p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return null;
};

export default AuthCallbackHandler;
